import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';

// Choreography (full motion), in ms from mount:
//   0    name fades up
//   850  letters condense into dots at their own centers
//   1150 dots glide into a ring around the page center (staggered formation)
//   1950 ring spins two full turns, dots take a comet-tail opacity grade
//   3000 veil lifts: the site mounts beneath, still overlaid by the ring
//   3160 the tail unravels and streams through the "Jabez Llave" navbar wordmark
//   4250 overlay retires
const T_CONDENSE = 850;
const T_FORM = 1150;
const T_SPIN = 1950;
const T_LIFT = 3000;
const T_FLIGHT = 3160;
const T_GONE = 4250;

const RING_RADIUS = 30;
const DOT_SIZE = 6;
const FLIGHT_STAGGER = 0.038;
const FLIGHT_DURATION = 0.55;

const NAME = 'Jabez Llave';

const LoaderContainer = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme, $lifted }) => ($lifted ? 'transparent' : theme.colors.background)};
  color: ${({ theme }) => theme.colors.ink};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  transition: background 0.4s ease;
  pointer-events: ${({ $lifted }) => ($lifted ? 'none' : 'auto')};
  visibility: ${({ $gone }) => ($gone ? 'hidden' : 'visible')};
  opacity: ${({ $fadeOut }) => ($fadeOut ? 0 : 1)};
  ${({ $fadeOut }) => ($fadeOut ? 'transition: opacity 0.45s ease, background 0.4s ease;' : '')}
  overflow: hidden;
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const LogoText = styled.div`
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 400;
  font-size: clamp(2.4rem, 4.8vw, 3.4rem);
  line-height: 1;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.ink};
  animation: ${fadeUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
  white-space: pre;
`;

const Letter = styled(motion.span)`
  display: inline-block;
  will-change: transform, opacity;
`;

const Ring = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  will-change: transform;
`;

const Dot = styled(motion.span)`
  position: absolute;
  top: ${-DOT_SIZE / 2}px;
  left: ${-DOT_SIZE / 2}px;
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.accent};
  will-change: transform, opacity;
`;

const formationEase = [0.34, 1.3, 0.64, 1]; // arrives with a soft overshoot
const settleEase = [0.22, 1, 0.36, 1];

// The navbar brand is a styled-components span, so find it by its text.
const findNavbarName = () => {
  const candidates = document.querySelectorAll('nav a, nav span, header a, header span');
  for (const el of candidates) {
    if (el.textContent.trim() === NAME) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0) return rect;
    }
  }
  return null;
};

const Loader = ({ setIsLoading }) => {
  const shouldReduceMotion = useReducedMotion();
  const [phase, setPhase] = useState('intro'); // intro → condense → form → spin → lift → flight → gone
  const [origins, setOrigins] = useState(null); // per-letter offsets from screen center
  const [target, setTarget] = useState(null); // navbar wordmark rect, in center-offset space
  const [reducedDone, setReducedDone] = useState(false);
  const letterRefs = useRef([]);

  const letters = useMemo(() => NAME.split(''), []);
  const dotCount = useMemo(() => letters.filter((c) => c !== ' ').length, [letters]);

  // Ring slots: letters flow left-to-right onto a clockwise ring, starting at 12 o'clock.
  const slots = useMemo(
    () =>
      Array.from({ length: dotCount }, (_, i) => {
        const angle = (-90 + (360 / dotCount) * i) * (Math.PI / 180);
        return {
          x: RING_RADIUS * Math.cos(angle),
          y: RING_RADIUS * Math.sin(angle),
        };
      }),
    [dotCount]
  );

  // Measure each letter's center relative to the viewport center once fonts settle,
  // so every dot is born exactly where its letter's ink was.
  useEffect(() => {
    if (shouldReduceMotion) return undefined;
    let cancelled = false;

    const measure = () => {
      if (cancelled) return;
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const next = [];
      letters.forEach((char, i) => {
        if (char === ' ') return;
        const node = letterRefs.current[i];
        if (!node) return;
        const rect = node.getBoundingClientRect();
        next.push({ x: rect.left + rect.width / 2 - cx, y: rect.top + rect.height / 2 - cy });
      });
      if (next.length === dotCount) setOrigins(next);
    };

    const ready = document.fonts?.ready || Promise.resolve();
    ready.then(() => requestAnimationFrame(measure));
    const fallback = setTimeout(measure, 700);

    return () => {
      cancelled = true;
      clearTimeout(fallback);
    };
  }, [letters, dotCount, shouldReduceMotion]);

  useEffect(() => {
    if (shouldReduceMotion) {
      window.__splashActive = false;
      const complete = setTimeout(() => {
        setReducedDone(true);
        setTimeout(() => setIsLoading(false), 450);
      }, 900);
      return () => clearTimeout(complete);
    }

    // Tells the navbar to hold its wordmark back until the tail writes it in.
    window.__splashActive = true;

    const timers = [
      setTimeout(() => setPhase('condense'), T_CONDENSE),
      setTimeout(() => setPhase('form'), T_FORM),
      setTimeout(() => setPhase('spin'), T_SPIN),
      setTimeout(() => {
        // The site mounts beneath the (now transparent) veil while the ring holds.
        setPhase('lift');
        setIsLoading(false);
      }, T_LIFT),
      setTimeout(() => {
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        const rect = findNavbarName();
        setTarget(
          rect
            ? { left: rect.left - cx, width: rect.width, y: rect.top + rect.height / 2 - cy }
            : { left: 32 - cx, width: 120, y: 30 - cy }
        );
        setPhase('flight');
        // Clip-reveal the navbar wordmark left-to-right, timed to the dot landings:
        // the head lands ~550ms after launch, the last dot ~340ms later.
        window.dispatchEvent(
          new CustomEvent('brand-reveal', { detail: { delay: 500, duration: 400 } })
        );
      }, T_FLIGHT),
      setTimeout(() => {
        setPhase('gone');
        window.__splashActive = false;
      }, T_GONE),
    ];
    return () => timers.forEach(clearTimeout);
  }, [setIsLoading, shouldReduceMotion]);

  // Without measurements (or with reduced motion) the splash stays a calm wordmark.
  const formation = !shouldReduceMotion && origins !== null;
  const lettersGone = formation && phase !== 'intro';

  let dotIndex = -1;

  return (
    <LoaderContainer
      $lifted={phase === 'lift' || phase === 'flight' || phase === 'gone'}
      $gone={phase === 'gone'}
      $fadeOut={shouldReduceMotion && reducedDone}
      role="status"
      aria-label="Loading"
    >
      <LogoText aria-label={NAME}>
        {letters.map((char, i) => (
          <Letter
            key={`${char}-${i}`}
            aria-hidden="true"
            ref={(node) => {
              letterRefs.current[i] = node;
            }}
            animate={
              lettersGone
                ? { opacity: 0, scale: 0.4, transition: { duration: 0.3, ease: settleEase, delay: (i * 28) / 1000 } }
                : { opacity: 1, scale: 1 }
            }
          >
            {char}
          </Letter>
        ))}
      </LogoText>

      {formation && (
        <Ring
          aria-hidden="true"
          animate={
            phase === 'spin' || phase === 'lift'
              ? { rotate: 720, transition: { duration: 1.0, ease: [0.6, 0.05, 0.4, 0.95] } }
              : phase === 'flight' || phase === 'gone'
              ? { rotate: 720, transition: { duration: 0 } }
              : { rotate: 0 }
          }
        >
          {letters.map((char, i) => {
            if (char === ' ') return null;
            dotIndex += 1;
            const d = dotIndex;
            const origin = origins[d];
            const slot = slots[d];
            const tail = 0.3 + (0.7 * d) / Math.max(dotCount - 1, 1);
            // The bright head of the tail leads the flight and lands on the left
            // edge of the wordmark; the rest streams through it left to right.
            const k = dotCount - 1 - d;

            let animate;
            if (phase === 'condense') {
              animate = {
                x: origin.x,
                y: origin.y,
                scale: 1,
                opacity: 1,
                transition: { duration: 0.26, ease: settleEase, delay: (d * 28) / 1000 },
              };
            } else if (phase === 'form') {
              animate = {
                x: slot.x,
                y: slot.y,
                scale: 1,
                opacity: 1,
                transition: { duration: 0.62, ease: formationEase, delay: (d * 36) / 1000 },
              };
            } else if (phase === 'spin' || phase === 'lift') {
              animate = {
                x: slot.x,
                y: slot.y,
                scale: 1,
                opacity: tail,
                transition: { duration: 0.3, ease: 'easeOut' },
              };
            } else if ((phase === 'flight' || phase === 'gone') && target) {
              const tx = target.left + (k / Math.max(dotCount - 1, 1)) * target.width;
              const ty = target.y;
              // One shared swoop: bow each path perpendicular to its line of flight.
              const dx = tx - slot.x;
              const dy = ty - slot.y;
              const len = Math.hypot(dx, dy) || 1;
              let px = -dy / len;
              let py = dx / len;
              if (py < 0) {
                px = -px;
                py = -py;
              }
              const mx = slot.x + dx * 0.45 + px * 56;
              const my = slot.y + dy * 0.45 + py * 56;
              const delay = k * FLIGHT_STAGGER;
              // Per-segment eases keep the swoop continuous: accelerate out of the
              // ring, decelerate into the wordmark — no hitch at the waypoint.
              const path = { duration: FLIGHT_DURATION, delay, times: [0, 0.5, 1], ease: ['easeIn', 'easeOut'] };
              animate = {
                x: [slot.x, mx, tx],
                y: [slot.y, my, ty],
                scale: [1, 0.95, 0.5],
                opacity: [tail, 1, 1, 0],
                transition: {
                  x: path,
                  y: path,
                  scale: { duration: FLIGHT_DURATION, delay, times: [0, 0.5, 1], ease: 'easeInOut' },
                  // Stay bright through the flight; vanish only on arrival.
                  opacity: { duration: FLIGHT_DURATION, delay, times: [0, 0.25, 0.82, 1], ease: 'linear' },
                },
              };
            } else {
              animate = { x: origin.x, y: origin.y, scale: 0, opacity: 0 };
            }

            return (
              <Dot
                key={`dot-${i}`}
                initial={{ x: origin.x, y: origin.y, scale: 0, opacity: 0 }}
                animate={animate}
              />
            );
          })}
        </Ring>
      )}
    </LoaderContainer>
  );
};

export default Loader;
